// 剧目销售报表 - 入口
// 接口约定：2026-09-01-show-sales-dashboard-frontend-api.md
// 入参：{ proNo, startDate, endDate }
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { useDispatch, useSelector, useStore } from 'dva';
import dayjs from 'dayjs';
import { PageWrap } from './components/styled';
import PageHeader from './components/page_header';
import SalesOverview from './components/sales_overview';
import VisitSalesTrend from './components/visit_sales_trend';
import PurchaseFunnel from './components/purchase_funnel';
import GeoTop5 from './components/geo_top5';
import SessionSales from './components/session_sales';
import ChannelSplit from './components/channel_split';
import CaliberNote from './components/caliber_note';

// 默认统计时间：最近 3 个月（含首尾共 90 天）
const defaultStart = dayjs().subtract(89, 'day').format('YYYY-MM-DD');
const defaultEnd = dayjs().format('YYYY-MM-DD');

export default () => {
  const dispatch = useDispatch();
  const store = useStore();
  const loadingEffect = useSelector((s) => s.loading);
  const [isInit, setIsInit] = useState(false);

  // 查询条件（受控，存于 store）
  const proNo = useSelector((s) => s.performSales.proNo);
  const dateStart = useSelector((s) => s.performSales.dateStart);
  const dateEnd = useSelector((s) => s.performSales.dateEnd);

  // 剧目下拉
  const rawPlayList = useSelector((s) => s.performSales.playList);
  const playList = useMemo(() => rawPlayList || [], [rawPlayList]);

  // 各接口数据
  const overview = useSelector((s) => s.performSales.overview);
  const vip = useSelector((s) => s.performSales.vip);
  const trend = useSelector((s) => s.performSales.trend);
  const funnel = useSelector((s) => s.performSales.funnel);
  const geoTop5 = useSelector((s) => s.performSales.geoTop5);
  const sessions = useSelector((s) => s.performSales.sessions);
  const channelSplit = useSelector((s) => s.performSales.channelSplit);

  // 拉取所有业务接口
  const fetchAll = useCallback(
    (params) => {
      dispatch({ type: 'performSales/getShowOverview', payload: params });
      dispatch({ type: 'performSales/getShowVip', payload: params });
      dispatch({
        type: 'performSales/getVisitSalesTrend',
        payload: params,
      });
      dispatch({ type: 'performSales/getShowFunnel', payload: params });
      dispatch({ type: 'performSales/getShowRegionTop', payload: params });
      dispatch({ type: 'performSales/getShowSessionStack', payload: params });
      // 渠道仅文广剧目才展示：接口会按 wentIncluded 自动返回 0/空行；
      // 仍可调用，前端按 channelSplit.wentIncluded 决定是否渲染。
      dispatch({ type: 'performSales/getShowChannel', payload: params });
    },
    [dispatch],
  );

  // 初次进入：拉剧目下拉 + 写入默认时间
  useEffect(() => {
    dispatch({ type: 'performSales/getPlayList' });
    if (!dateStart || !dateEnd) {
      dispatch({
        type: 'performSales/save',
        payload: {
          dateStart: defaultStart,
          dateEnd: defaultEnd,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 列表回来且 proNo 仍为空时：默认选中第一项
  useEffect(() => {
    if (!proNo && playList.length > 0) {
      dispatch({
        type: 'performSales/save',
        payload: { proNo: playList[0].proNo },
      });
    }
  }, [playList, proNo, dispatch]);

  // 查询条件变化时自动重新拉取
  const params = useMemo(
    () => ({
      proNo,
      startDate: dateStart,
      endDate: dateEnd,
    }),
    [proNo, dateStart, dateEnd],
  );
  useEffect(() => {
    if (!isInit && proNo && dateStart && dateEnd) {
      fetchAll(params);
      setIsInit(true);
    }
  }, [fetchAll, params, proNo, dateStart, dateEnd, isInit]);

  const handleSearch = () => {
    const latest = store.getState().performSales || {};
    const latestParams = {
      proNo: latest.proNo,
      startDate: latest.dateStart,
      endDate: latest.dateEnd,
    };
    fetchAll(latestParams);
  };

  const handleReset = () => {
    dispatch({
      type: 'performSales/save',
      payload: {
        proNo: playList[0]?.proNo || '',
        dateStart: defaultStart,
        dateEnd: defaultEnd,
      },
    });
    handleSearch();
  };

  const showChannel = channelSplit?.wentIncluded === true;

  // 各接口 loading：与 dashboard 同名模式（effects key = namespace/effectName）
  const overviewLoading = loadingEffect.effects['performSales/getShowOverview'];
  const vipLoading = loadingEffect.effects['performSales/getShowVip'];
  const trendLoading = loadingEffect.effects['performSales/getVisitSalesTrend'];
  const funnelLoading = loadingEffect.effects['performSales/getShowFunnel'];
  const geoLoading = loadingEffect.effects['performSales/getShowRegionTop'];
  const sessionLoading =
    loadingEffect.effects['performSales/getShowSessionStack'];
  const channelLoading = loadingEffect.effects['performSales/getShowChannel'];

  return (
    <Box
      data-log={JSON.stringify({
        oid: 'page_report_center_perform_sales',
        isPage: true,
        params: {
          initUrl: window.location.origin + window.location.pathname,
        },
        events: ['_pv', '_pd'],
      })}
      id="perform_sales_mount_parent"
    >
      <PageWrap>
        <PageHeader
          proNo={proNo}
          dateStart={dateStart}
          dateEnd={dateEnd}
          playList={playList}
          setProNo={(v) =>
            dispatch({ type: 'performSales/save', payload: { proNo: v } })
          }
          setDateStart={(v) =>
            dispatch({ type: 'performSales/save', payload: { dateStart: v } })
          }
          setDateEnd={(v) =>
            dispatch({ type: 'performSales/save', payload: { dateEnd: v } })
          }
          onSearch={handleSearch}
          onReset={handleReset}
        />

        <SalesOverview
          overview={overview}
          vip={vip}
          loading={overviewLoading || vipLoading}
        />
        <VisitSalesTrend trend={trend} loading={trendLoading} />
        <PurchaseFunnel data={funnel} loading={funnelLoading} />
        <GeoTop5 data={geoTop5} loading={geoLoading} />
        <SessionSales data={sessions} loading={sessionLoading} />
        {showChannel && (
          <ChannelSplit data={channelSplit} loading={channelLoading} />
        )}

        <CaliberNote data={overview} />
      </PageWrap>
    </Box>
  );
};
